import { useEffect, useState } from 'react';
import { Slider } from './ui/slider';
import { t } from '../i18n';

interface Props {
  min: number;
  max: number;
  from: number;
  to: number;
  onChange: (from: number, to: number) => void;
  label?: string;
}

/** Dual-thumb accessible slider built with Radix UI and APUA design tokens. */
export function RangeSlider({ min, max, from, to, onChange, label = t.yearRange }: Props) {
  const [localVal, setLocalVal] = useState<[number, number]>([from, to]);

  useEffect(() => {
    setLocalVal([from, to]);
  }, [from, to]);

  return (
    <div className="space-y-3 pt-1" role="group" aria-label={label}>
      <div className="flex items-center justify-between text-xs">
        <span className="font-serif text-sm font-semibold text-gold2 flex items-baseline gap-1">
          {localVal[0]}
          <span className="font-sans text-[10px] font-normal text-dim uppercase tracking-wider">
            {t.rangeFrom}
          </span>
        </span>
        <span className="font-serif text-sm font-semibold text-gold2 flex items-baseline gap-1">
          <span className="font-sans text-[10px] font-normal text-dim uppercase tracking-wider">
            {t.rangeTo}
          </span>
          {localVal[1]}
        </span>
      </div>

      <Slider
        min={min}
        max={max}
        step={1}
        value={localVal}
        onValueChange={(val) => {
          const [newFrom, newTo] = val;
          if (newFrom !== undefined && newTo !== undefined) {
            setLocalVal([newFrom, newTo]);
          }
        }}
        onValueCommit={(val) => {
          const [committedFrom, committedTo] = val;
          if (committedFrom !== undefined && committedTo !== undefined) {
            onChange(committedFrom, committedTo);
          }
        }}
        aria-label={label}
      />
    </div>
  );
}

export interface SingleYearSliderProps {
  min: number;
  max: number;
  value: number;
  onChange: (year: number) => void;
  label?: string;
}

/** Single-thumb accessible slider matching RangeSlider aesthetics for single-year selection. */
export function SingleYearSlider({
  min,
  max,
  value,
  onChange,
  label = t.year,
}: SingleYearSliderProps) {
  const [localVal, setLocalVal] = useState<number>(value);

  useEffect(() => {
    setLocalVal(value);
  }, [value]);

  return (
    <div className="space-y-3 pt-1" role="group" aria-label={label}>
      <div className="flex items-center justify-between text-xs">
        <span className="font-sans text-[10px] font-normal text-dim uppercase tracking-wider">
          {label}
        </span>
        <span className="font-serif text-base font-bold text-gold2">
          {localVal}
        </span>
      </div>

      <Slider
        min={min}
        max={max}
        step={1}
        value={[localVal]}
        onValueChange={(val) => {
          const newY = val[0];
          if (newY !== undefined) {
            setLocalVal(newY);
          }
        }}
        onValueCommit={(val) => {
          const committed = val[0];
          if (committed !== undefined) {
            onChange(committed);
          }
        }}
        aria-label={label}
      />

      <div className="flex items-center justify-between text-[10px] font-mono text-dim px-0.5">
        <span>{min}</span>
        <span>{max}</span>
      </div>
    </div>
  );
}

